import React from "react"
import PropTypes from "prop-types"
import i18n from "i18n"
import { Typography, Box } from "@mui/material"

import { formatChartNumber } from "./utils/numberFormat"
import OverviewCard, {
  overviewCardValueSx,
  overviewCardHelperTextSx,
} from "./OverviewCard"

const formatSignedPercent = value => {
  if (value === null || !Number.isFinite(value)) return null
  const abs = Math.abs(value)
  const sign = value > 0 ? "+" : value < 0 ? "−" : ""
  return `${sign}${abs.toFixed(1).replace(".", ",")}%`
}

const formatSignedAmount = (value, currency) => {
  const abs = formatChartNumber(Math.abs(value), 2)
  const sign = value > 0 ? "+" : value < 0 ? "−" : ""
  return `${sign}${abs} ${currency ?? ""}`.trim()
}

const FinalExpectedMarginCard = ({
  marginAbsolute,
  rentabilityPercent,
  vsObjectiveMarginGap,
  isLoading,
  currency,
}) => {
  const percentStr = formatSignedPercent(rentabilityPercent)
  const vsObjectiveAmount = formatSignedAmount(vsObjectiveMarginGap, currency)

  const summaryParts = []
  if (percentStr !== null) {
    summaryParts.push(
      i18n.t("overview.controlFinalExpectedMarginHelper", {
        percent: percentStr,
      }),
    )
  }
  summaryParts.push(
    i18n.t("overview.controlFinalExpectedMarginVsObjective", {
      amount: vsObjectiveAmount,
    }),
  )
  const summaryText = summaryParts.join(" · ")

  const tooltipTitle = (
    <Box sx={{ maxWidth: 340, whiteSpace: "pre-line" }}>
      {i18n.t("overview.controlFinalExpectedMarginTooltip")}
    </Box>
  )

  return (
    <OverviewCard
      iconPreset="finalExpectedMargin"
      title={i18n.t("overview.controlFinalExpectedMarginTitle")}
      infoText={tooltipTitle}
      loading={isLoading}
      sx={{ height: "100%" }}
    >
      <Typography sx={overviewCardValueSx}>
        {formatChartNumber(marginAbsolute, 2)} {currency}
      </Typography>
      <Typography variant="body2" sx={overviewCardHelperTextSx}>
        {summaryText}
      </Typography>
    </OverviewCard>
  )
}

FinalExpectedMarginCard.propTypes = {
  marginAbsolute: PropTypes.number.isRequired,
  rentabilityPercent: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.oneOf([null]),
  ]),
  vsObjectiveMarginGap: PropTypes.number,
  isLoading: PropTypes.bool,
  currency: PropTypes.string,
}

FinalExpectedMarginCard.defaultProps = {
  vsObjectiveMarginGap: 0,
}

export default FinalExpectedMarginCard
